/**
 * Branding assets from General Settings (system-setting type=generalSettings).
 */
import axios from "axios";

export type BrandingSettings = {
  name: string | null;
  logoLtr: string | null;
  logoRtl: string | null;
  logoCollapsed: string | null;
  favicon: string | null;
};

const EMPTY: BrandingSettings = {
  name: null,
  logoLtr: null,
  logoRtl: null,
  logoCollapsed: null,
  favicon: null,
};

const STORAGE_KEY = "app_branding";

let cached: BrandingSettings | null = null;
let publicLoadPromise: Promise<BrandingSettings> | null = null;

function readStoredBranding(): BrandingSettings | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      name: typeof parsed?.name === "string" && parsed.name.trim() ? parsed.name.trim() : null,
      logoLtr: parsed?.logoLtr || null,
      logoRtl: parsed?.logoRtl || null,
      logoCollapsed: parsed?.logoCollapsed || null,
      favicon: parsed?.favicon || null,
    };
  } catch {
    return null;
  }
}

function persistBranding(branding: BrandingSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(branding));
  } catch {
    // ignore quota / private mode
  }
}

export function hasConfiguredLogo(branding: BrandingSettings | null | undefined): boolean {
  if (!branding) return false;
  return !!(branding.logoLtr || branding.logoRtl || branding.logoCollapsed);
}

export function getCachedBranding(): BrandingSettings {
  if (cached) return cached;
  const stored = readStoredBranding();
  if (stored) {
    cached = stored;
    return cached;
  }
  return { ...EMPTY };
}

export function setCachedBranding(next: Partial<BrandingSettings>): BrandingSettings {
  cached = { ...getCachedBranding(), ...next };
  persistBranding(cached);
  publicLoadPromise = null;
  return cached;
}

export function clearCachedBranding(): void {
  cached = null;
  publicLoadPromise = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function mapGeneralSettingsToBranding(row: Record<string, any> | null | undefined): BrandingSettings {
  if (!row) return { ...EMPTY };
  const name =
    typeof row.name === "string" && row.name.trim() ? row.name.trim() : null;
  return {
    name,
    logoLtr: row.FullImagePathLogo || row.logoLtr || null,
    logoRtl: row.FullImagePathLogoRtl || row.logoRtl || null,
    logoCollapsed: row.FullImagePathLogoCollapsed || row.logoCollapsed || null,
    favicon: row.FullImagePathFavicon || row.favicon || null,
  };
}

/** Load singleton general settings branding (requires auth). */
export async function loadBrandingFromGeneralSettings(): Promise<BrandingSettings> {
  try {
    const { default: GeneralSettings } = await import(
      "@/API/GeneralSettings/GeneralSettings"
    );
    const api = new GeneralSettings();
    const rows = (await api.getAll({
      filter: `type|generalSettings|=`,
    })) as Array<Record<string, any>>;
    const branding = mapGeneralSettingsToBranding(rows?.[0]);
    setCachedBranding(branding);
    return branding;
  } catch {
    return getCachedBranding();
  }
}

/**
 * Load branding without auth (login page).
 * Prefers config from API; falls back to localStorage cache on network error.
 */
export async function loadPublicBranding(): Promise<BrandingSettings> {
  if (publicLoadPromise) {
    return publicLoadPromise;
  }

  publicLoadPromise = (async () => {
    const fallback = getCachedBranding();
    try {
      const base = (process.env.VUE_APP_API_BASE_URL || "").replace(/\/?$/, "/");
      const response = await axios.get(`${base}system-setting/public-branding`, {
        transformRequest: [
          (data, headers) => {
            if (headers) {
              delete headers.Authorization;
              delete headers.authorization;
            }
            return data;
          },
        ],
      });
      const branding = mapGeneralSettingsToBranding(response?.data);
      setCachedBranding(branding);
      return branding;
    } catch {
      return fallback;
    }
  })();

  return publicLoadPromise;
}

/**
 * Resolve logo for direction.
 * Config logos first; fallbacks only when provided and no config logo exists.
 */
export function pickLogoForDirection(
  branding: BrandingSettings,
  isRtl: boolean,
  fallbackLtr?: string | null,
  fallbackRtl?: string | null
): string | null {
  if (isRtl) {
    return branding.logoRtl || branding.logoLtr || fallbackRtl || fallbackLtr || null;
  }
  return branding.logoLtr || fallbackLtr || null;
}

export function detectIsRtl(): boolean {
  return (
    document.documentElement.dir === "rtl" ||
    document.documentElement.getAttribute("lang") === "ar" ||
    localStorage.getItem("selectedLanguageCode") === "ar"
  );
}
